import { useCallback, useEffect, useRef } from 'react';

/**
 * Autosave con debounce para formularios del peritaje.
 *
 * Uso:
 *   const autosave = useAutosave(async () => { await post(url, body); });
 *   - en cada cambio:            autosave.schedule();
 *   - al guardar explícito:      autosave.cancel();  // evita doble guardado
 *   - para forzar guardado ya:   autosave.flush();
 *
 * Varios cambios rápidos se agrupan en un solo guardado (debounce). Si el
 * formulario se desmonta con cambios pendientes, se guarda para no perder datos.
 */
export default function useAutosave(saveFn, delay = 1500) {
  const saveFnRef = useRef(saveFn);
  const timerRef = useRef(null);
  const pendingRef = useRef(false);

  useEffect(() => {
    saveFnRef.current = saveFn;
  });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    clearTimer();
    if (!pendingRef.current) return;
    pendingRef.current = false;
    saveFnRef.current();
  }, [clearTimer]);

  const schedule = useCallback(() => {
    pendingRef.current = true;
    clearTimer();
    timerRef.current = setTimeout(flush, delay);
  }, [clearTimer, flush, delay]);

  const cancel = useCallback(() => {
    clearTimer();
    pendingRef.current = false;
  }, [clearTimer]);

  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        clearTimer();
        pendingRef.current = false;
        saveFnRef.current();
      }
    };
  }, [clearTimer]);

  return { schedule, flush, cancel };
}
